import { useAnalytics } from '@cosuno/analytics-frontend';
import { isEnter, isEntered, isExited, isExiting, Portal } from '@cosuno/cosuno-ui';
import { KEY_NAME, useKeyboardEventCallback } from '@cosuno/react-hooks';
import { NavigationConfirmationEnvironmentContext } from '@cosuno/react-router';
import { fromPairs, toPairs } from 'lodash-es';
import React, { type ReactNode, useEffect, useMemo, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { mergeRefs } from 'react-merge-refs';
import { Transition } from 'react-transition-group';

import { MODAL_VARIANTS } from './constants';
import {
  Card,
  CloseButton,
  Layout,
  LayoutInner,
  Overlay,
  ScrollableCardContent,
  TRANSITION_DURATION,
} from './Modal.styles';
import type { ModalStateWithParamsData } from './useModalStateWithParams';

export interface ModalProps<Params> extends Omit<ModalStateWithParamsData<Params>, 'events'> {
  name?: string;
  className?: string;
  variant?: MODAL_VARIANTS;
  trapFocus?: boolean;
  renderContent: (options: { isReadyToLoad: boolean; params: Params }) => ReactNode;
  forwardRef?: React.MutableRefObject<HTMLDivElement | null>;
  hasPrimaryColorOverlay?: boolean;
  // Enables gradient for Modal card (instead of a plain white color)
  hasGradient?: boolean;
  // By default, we don't allow content escaping from the Modal card.
  // However, in some cases (like standardized upsell modal for gold package) it's required by design.
  allowOverflow?: boolean;
  isTopLevelModal?: boolean;
  maxWidth?: number;
  isScrollableWithin?: boolean;
  hasNoRoundedCorners?: boolean;
}

export const Modal = <Params,>({
  name,
  className,
  isActive,
  isOpen,
  id,
  isScrollableWithin,
  closeModal,
  openModal: _,
  isClosingDisabled,
  variant = MODAL_VARIANTS.center,
  renderContent,
  navigationConfirmationEnvironmentId,
  isNavigatingAway,
  subscribeToForm,
  forwardRef,
  hasPrimaryColorOverlay,
  hasGradient,
  allowOverflow,
  isTopLevelModal,
  params,
  maxWidth,
  trapFocus = true,
  tryNavigatingAway,
  hasNoRoundedCorners,
  ...otherProps
}: ModalProps<Params>): ReturnType<React.FC<ModalProps<Params>>> => {
  const dataAttributes = fromPairs(toPairs(otherProps).filter(([key]) => key.startsWith('data')));
  const { trackEvent } = useAnalytics();

  const transitionNodeRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = forwardRef ? mergeRefs([forwardRef, transitionNodeRef]) : transitionNodeRef;

  const modalViewTracked = useRef(false);
  useEffect(() => {
    // only care for named modals
    if (!name) return;

    if (isOpen) {
      if (modalViewTracked.current) return;
      modalViewTracked.current = true;

      trackEvent('modalView', { name });
    } else {
      // reset to false in case we closed the modal
      modalViewTracked.current = false;
    }
  }, [isOpen, name, trackEvent]);

  const navigationConfirmationEnvironmentContextValue = useMemo(
    () => ({
      subscribeToForm,
      environmentId: navigationConfirmationEnvironmentId,
      isNavigatingAway,
      tryNavigatingAway,
    }),
    [isNavigatingAway, navigationConfirmationEnvironmentId, subscribeToForm, tryNavigatingAway],
  );

  useKeyboardEventCallback(window, KEY_NAME.escape, () => closeModal('esc-key'), {
    condition: isOpen && isActive && !isClosingDisabled,
  });

  return (
    <NavigationConfirmationEnvironmentContext.Provider
      value={navigationConfirmationEnvironmentContextValue}
    >
      <Portal>
        <Transition
          in={isOpen}
          timeout={TRANSITION_DURATION}
          appear
          mountOnEnter
          unmountOnExit
          nodeRef={transitionNodeRef}
        >
          {(state) => {
            if (params === null) {
              return null;
            }

            const isReadyToLoad = isEntered(state) || isExiting(state);

            const content = (
              <div ref={wrapperRef}>
                <Overlay
                  $transitionState={state}
                  $isActive={isActive || isExiting(state)}
                  $isTopLevelModal={isTopLevelModal}
                  $hasPrimaryColorOverlay={hasPrimaryColorOverlay}
                />
                <Layout
                  data-testid="modal-layout"
                  data-test-is-active={isActive}
                  data-cy-is-active-modal={isActive}
                  data-cy-is-open-modal={isEntered(state)}
                  data-cy-modal-name={name}
                  $variant={variant}
                  $transitionState={state}
                  $isActive={isActive || isExiting(state)}
                  $isTopLevelModal={isTopLevelModal}
                  {...dataAttributes}
                >
                  <LayoutInner $transitionState={state}>
                    <Card
                      $maxWidth={maxWidth}
                      $isScrollableWithin={isScrollableWithin}
                      $hasGradient={hasGradient}
                      $allowOverflow={allowOverflow}
                      $hasNoRoundedCorners={hasNoRoundedCorners}
                      className={className}
                      variant={variant}
                      transitionState={state}
                    >
                      {!isExited(state) && !isClosingDisabled && (
                        <CloseButton
                          data-cy-close-modal
                          data-testid="modal-close-button"
                          onlyIcon={{
                            type: 'close',
                            size: 16,
                          }}
                          size="small"
                          variant="secondary"
                          hidden={variant === MODAL_VARIANTS.right}
                          onClick={() => closeModal('close-button')}
                        />
                      )}
                      {!isExited(state) && (
                        <ScrollableCardContent $allowOverflow={allowOverflow} data-cy-modal-body>
                          {renderContent({
                            isReadyToLoad,
                            params,
                          })}
                        </ScrollableCardContent>
                      )}
                    </Card>
                  </LayoutInner>
                </Layout>
              </div>
            );

            if (!trapFocus) {
              return content;
            }

            return (
              <FocusLock disabled={!isEnter(state)} autoFocus={false}>
                {content}
              </FocusLock>
            );
          }}
        </Transition>
      </Portal>
    </NavigationConfirmationEnvironmentContext.Provider>
  );
};
